import React, { ChangeEvent, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Button,
    CardMedia,
    IconButton,
    Input,
    TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useCreateDispatcherMutation, useUpdateDispatcherMutation } from "../../store/api/manageAPI";
import { useUploadFileMutation } from "../../store/api/commonAPI";
import Loader from "../../constants/Loader";
import { showToast, showError } from "../../constants/toasts";

const AddDispatcher = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const editDispatcher = location.state?.dispatcher;
    const isEditMode = !!editDispatcher;

    // API hooks
    const [createDispatcher, { isLoading: isCreating }] = useCreateDispatcherMutation();
    const [updateDispatcher, { isLoading: isUpdating }] = useUpdateDispatcherMutation();
    const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

    // Profile Image
    const [image, setImage] = useState<string>("");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");

    // Form fields - only the fields that exist in UI
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");
    const [country, setCountry] = useState("");
    const [state, setState] = useState("");
    const [city, setCity] = useState("");
    const [pinCode, setPinCode] = useState("");

    // Error states
    const [errors, setErrors] = useState<{
        name?: string;
        email?: string;
        phone?: string;
        address?: string;
        country?: string;
        state?: string;
        city?: string;
        pinCode?: string;
        image?: string;
    }>({});

    // Load edit data
    useEffect(() => {
        if (editDispatcher) {
            setName(editDispatcher.fullName || editDispatcher.name || "");
            setEmail(editDispatcher.email || "");
            setPhone(editDispatcher.phone || "");
            setAddress(editDispatcher.address?.addressLine1 || editDispatcher.address || "");
            setCountry(editDispatcher.address?.country || "");
            setState(editDispatcher.address?.state || "");
            setCity(editDispatcher.address?.city || "");
            setPinCode(editDispatcher.address?.pinCode || "");
            if (editDispatcher.image) {
                setImage(editDispatcher.image);
                setUploadedImageUrl(editDispatcher.image);
            }
            // Clear errors when loading edit data
            setErrors({});
        }
    }, [editDispatcher]);

    // Handle profile image upload
    const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = (event.target as HTMLInputElement).files;
        if (files && files[0]) {
            // Validate file type
            if (!files[0].type.includes("image")) {
                setErrors(prev => ({ ...prev, image: "Please upload a valid image file (PNG or JPEG)" }));
                return;
            }
            // Validate file size (max 5MB)
            if (files[0].size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, image: "Image size should be less than 5MB" }));
                return;
            }
            setImageFile(files[0]);
            setImage(URL.createObjectURL(files[0]));
            setErrors(prev => ({ ...prev, image: undefined }));
        }
    };

    // Validation functions
    const validateName = (value: string): string | undefined => {
        if (!value.trim()) {
            return "Name is required";
        }
        if (value.trim().length < 2) {
            return "Name must be at least 2 characters";
        }
        if (value.trim().length > 50) {
            return "Name must be less than 50 characters";
        }
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
            return "Name should only contain letters and spaces";
        }
        return undefined;
    };

    const validateEmail = (value: string): string | undefined => {
        if (!value.trim()) {
            return "Email is required";
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.trim())) {
            return "Please enter a valid email address";
        }
        return undefined;
    };

    const validatePhone = (value: string): string | undefined => {
        if (!value.trim()) {
            return "Phone number is required";
        }
        // Allow digits, spaces, dashes, and parentheses
        const cleanedPhone = value.replace(/[\s\-\(\)]/g, '');
        if (cleanedPhone.length < 10) {
            return "Phone number must be at least 10 digits";
        }
        if (cleanedPhone.length > 15) {
            return "Phone number must be less than 15 digits";
        }
        return undefined;
    };

    const validateAddress = (value: string): string | undefined => {
        if (!value.trim()) {
            return "Address is required";
        }
        if (value.trim().length < 5) {
            return "Address must be at least 5 characters";
        }
        if (value.trim().length > 200) {
            return "Address must be less than 200 characters";
        }
        return undefined;
    };

    const validateCountry = (value: string): string | undefined => {
        if (!value.trim()) {
            return "Country is required";
        }
        if (value.trim().length < 2) {
            return "Country must be at least 2 characters";
        }
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
            return "Country should only contain letters and spaces";
        }
        return undefined;
    };

    const validateState = (value: string): string | undefined => {
        if (!value.trim()) {
            return "State is required";
        }
        if (value.trim().length < 2) {
            return "State must be at least 2 characters";
        }
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
            return "State should only contain letters and spaces";
        }
        return undefined;
    };

    const validateCity = (value: string): string | undefined => {
        if (!value.trim()) {
            return "City is required";
        }
        if (value.trim().length < 2) {
            return "City must be at least 2 characters";
        }
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
            return "City should only contain letters and spaces";
        }
        return undefined;
    };

    const validatePinCode = (value: string): string | undefined => {
        if (!value.trim()) {
            return "Pin code is required";
        }
        // Allow alphanumeric for international pin codes
        if (!/^[a-zA-Z0-9]+$/.test(value.trim())) {
            return "Pin code should only contain letters and numbers";
        }
        if (value.trim().length < 4) {
            return "Pin code must be at least 4 characters";
        }
        if (value.trim().length > 10) {
            return "Pin code must be less than 10 characters";
        }
        return undefined;
    };

    // Handle field changes with validation
    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setName(value);
        setErrors(prev => ({ ...prev, name: validateName(value) }));
    };

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);
        setErrors(prev => ({ ...prev, email: validateEmail(value) }));
    };

    const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPhone(value);
        setErrors(prev => ({ ...prev, phone: validatePhone(value) }));
    };

    const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAddress(value);
        setErrors(prev => ({ ...prev, address: validateAddress(value) }));
    };

    const handleCountryChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCountry(value);
        setErrors(prev => ({ ...prev, country: validateCountry(value) }));
    };

    const handleStateChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setState(value);
        setErrors(prev => ({ ...prev, state: validateState(value) }));
    };

    const handleCityChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCity(value);
        setErrors(prev => ({ ...prev, city: validateCity(value) }));
    };

    const handlePinCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPinCode(value);
        setErrors(prev => ({ ...prev, pinCode: validatePinCode(value) }));
    };

    // Form validation
    const validateForm = () => {
        const newErrors: typeof errors = {};

        // Validate all fields
        const nameError = validateName(name);
        const emailError = validateEmail(email);
        const phoneError = validatePhone(phone);
        const addressError = validateAddress(address);
        const countryError = validateCountry(country);
        const stateError = validateState(state);
        const cityError = validateCity(city);
        const pinCodeError = validatePinCode(pinCode);

        if (nameError) newErrors.name = nameError;
        if (emailError) newErrors.email = emailError;
        if (phoneError) newErrors.phone = phoneError;
        if (addressError) newErrors.address = addressError;
        if (countryError) newErrors.country = countryError;
        if (stateError) newErrors.state = stateError;
        if (cityError) newErrors.city = cityError;
        if (pinCodeError) newErrors.pinCode = pinCodeError;

        // Validate image (required for new dispatcher)
        if (!isEditMode && !image && !uploadedImageUrl) {
            newErrors.image = "Profile image is required";
        }

        setErrors(newErrors);

        // Check if there are any errors
        if (Object.keys(newErrors).length > 0) {
            const firstError = Object.values(newErrors).find(err => err);
            if (firstError) {
                showError(firstError);
            }
            return false;
        }

        return true;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            let imageUrl = uploadedImageUrl;

            // Upload profile image if a new file is selected
            if (imageFile) {
                const uploadResult = await uploadFile({ file: imageFile }).unwrap();
                imageUrl = uploadResult.data?.url || uploadResult.data?.fileUrl || uploadResult.data?.data || uploadResult.data;
                if (!imageUrl) {
                    showError("Failed to upload profile image");
                    return;
                }
            }

            // Prepare payload - only use fields from UI
            const nameParts = name.trim().split(' ');
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(' ') || "";

            const payload: any = {
                fullName: name.trim(),
                firstName: firstName,
                lastName: lastName,
                email: email.trim(),
                phone: phone.trim(),
                countryCode: "+1", // Default country code
                address: {
                    addressLine1: address.trim(),
                    city: city.trim(),
                    state: state.trim(),
                    country: country.trim(),
                    pinCode: pinCode.trim(),
                    type: 1,
                    isDefault: true,
                },
            };

            // Add image if available
            if (imageUrl) {
                payload.image = imageUrl;
            }

            // Create or update dispatcher
            let response;
            if (isEditMode && editDispatcher?._id) {
                response = await updateDispatcher({
                    id: editDispatcher._id,
                    ...payload,
                }).unwrap();
            } else {
                response = await createDispatcher(payload).unwrap();
            }

            const successMessage = response?.message || 
                `Dispatcher ${isEditMode ? "updated" : "created"} successfully`;
            showToast(successMessage);
            
            navigate("/manage-dispatcher");
        } catch (error: any) {
            console.error("Error saving dispatcher:", error);
            const errorMessage =
                error?.data?.message ||
                error?.error?.data?.message ||
                error?.message ||
                `Failed to ${isEditMode ? "update" : "create"} dispatcher. Please try again.`;
            showError(errorMessage);
        }
    };

    return (
        <>
            <Loader isLoad={isCreating || isUpdating || isUploading} />
            <div className="main_title">
                <h1>{isEditMode ? "Edit Dispatcher" : "Add Dispatcher"}</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/manage-dispatcher")}>
                        Manage Dispatchers
                    </button>{" "}
                    - {isEditMode ? "Edit" : "Add"} Dispatcher
                </p>
            </div>

            <div className="cards">
                <form onSubmit={handleSubmit}>
                    <div className="form">
                        <div className="gap_p">

                            {/* Profile Image */}
                            <div className="control_group w_100">
                                <div className="gap_p">
                                    <div className="control_group w_33">
                                        <label htmlFor="icon-button-file">Profile Image{!isEditMode ? "*" : ""}</label>
                                        {errors.image && (
                                            <div style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "4px" }}>
                                                {errors.image}
                                            </div>
                                        )}
                                        {image ? (
                                            <div className="upload_image">
                                                <div className="upload_image_holder">
                                                    <figure>
                                                        <CardMedia
                                                            component="img"
                                                            image={image}
                                                            alt="Uploaded profile photo"
                                                        />
                                                    </figure>
                                                    <IconButton
                                                        aria-label="Remove uploaded image"
                                                        onClick={() => {
                                                            setImage("");
                                                            setImageFile(null);
                                                            setUploadedImageUrl("");
                                                            setErrors(prev => ({ ...prev, image: undefined }));
                                                        }}
                                                        size="small"
                                                    >
                                                        <CloseIcon />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        ) : (
                                            <label
                                                className="upload_image"
                                                htmlFor="icon-button-file"
                                            >
                                                <Input
                                                    sx={{ display: "none" }}
                                                    id="icon-button-file"
                                                    type="file"
                                                    inputProps={{ accept: "image/png,image/jpeg" }}
                                                    onChange={handleImageChange}
                                                />
                                                <span className="upload_image_holder">
                                                    <figure className="add_img">
                                                        <img
                                                            src="/static/images/user_placeholder.png"
                                                            alt="placeholder"
                                                        />
                                                        <span className="upload_img_icon">
                                                            <img
                                                                src="/static/images/edit2_icon.svg"
                                                                alt="Upload"
                                                            />
                                                        </span>
                                                    </figure>
                                                </span>
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Name */}
                            <div className="control_group w_33">
                                <label htmlFor="name">Name*</label>
                                <TextField 
                                    id="name" 
                                    placeholder="Enter Name" 
                                    fullWidth 
                                    value={name}
                                    onChange={handleNameChange}
                                    error={!!errors.name}
                                    inputProps={{ maxLength: 50 }}
                                />
                            </div>

                            {/* Email */}
                            <div className="control_group w_33">
                                <label htmlFor="email">Email*</label>
                                <TextField 
                                    id="email" 
                                    placeholder="Enter Email" 
                                    type="email" 
                                    fullWidth 
                                    value={email}
                                    onChange={handleEmailChange}
                                    error={!!errors.email}
                                />
                            </div>

                            {/* Phone */}
                            <div className="control_group w_33">
                                <label htmlFor="phone">Phone Number*</label>
                                <TextField 
                                    id="phone" 
                                    placeholder="Enter Phone Number" 
                                    fullWidth 
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    error={!!errors.phone}
                                    inputProps={{ maxLength: 15 }}
                                />
                            </div>

                            {/* Address */}
                            <div className="control_group w_100">
                                <label htmlFor="address">Address*</label>
                                <TextField 
                                    id="address" 
                                    placeholder="Enter Address" 
                                    fullWidth 
                                    value={address}
                                    onChange={handleAddressChange}
                                    error={!!errors.address}
                                    inputProps={{ maxLength: 200 }}
                                />
                            </div>

                            {/* Country */}
                            <div className="control_group w_33">
                                <label htmlFor="country">Country*</label>
                                <TextField 
                                    id="country" 
                                    placeholder="Enter Country" 
                                    fullWidth 
                                    value={country}
                                    onChange={handleCountryChange}
                                    error={!!errors.country}
                                    inputProps={{ 
                                        maxLength: 50,
                                        pattern: "[a-zA-Z\\s]*"
                                    }}
                                />
                            </div>

                            {/* State */}
                            <div className="control_group w_33">
                                <label htmlFor="state">State*</label>
                                <TextField 
                                    id="state" 
                                    placeholder="Enter State" 
                                    fullWidth 
                                    value={state}
                                    onChange={handleStateChange}
                                    error={!!errors.state}
                                    inputProps={{ 
                                        maxLength: 50,
                                        pattern: "[a-zA-Z\\s]*"
                                    }}
                                />
                            </div>

                            {/* City */}
                            <div className="control_group w_33">
                                <label htmlFor="city">City*</label>
                                <TextField 
                                    id="city" 
                                    placeholder="Enter City" 
                                    fullWidth 
                                    value={city}
                                    onChange={handleCityChange}
                                    error={!!errors.city}
                                    inputProps={{ 
                                        maxLength: 50,
                                        pattern: "[a-zA-Z\\s]*"
                                    }}
                                />
                            </div>

                            {/* Pin Code */}
                            <div className="control_group w_33">
                                <label htmlFor="pin">Pin Code*</label>
                                <TextField 
                                    id="pin" 
                                    placeholder="Enter Pin Code" 
                                    fullWidth 
                                    value={pinCode}
                                    onChange={handlePinCodeChange}
                                    error={!!errors.pinCode}
                                    inputProps={{ 
                                        maxLength: 10,
                                        pattern: "[a-zA-Z0-9]*"
                                    }}
                                />
                            </div>

                        </div>

                        {/* Submit Button */}
                        <div className="form_btn">
                            <Button 
                                type="submit" 
                                className="btn btn_primary"
                                disabled={isCreating || isUpdating || isUploading}
                            >
                                {isEditMode ? "Update" : "Save"}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default AddDispatcher;
